import React from 'react';
import { Code, GitBranch, Phone, Clock, MessageSquare, Mail, DollarSign, MoreHorizontal } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface WorkflowsContentProps {
  animationDelay?: number;
}

// Vertical workflow tree structure
const workflowNodes = [
  // Row 1 - Trigger
  { id: 'trigger', row: 0, col: 1, icon: Code, label: 'Did the contact reply?', type: 'condition', color: '#6B7280' },
  
  // Row 2 - Branches
  { id: 'replied', row: 1, col: 0, icon: GitBranch, label: 'Contact replied', subtitle: 'If "Contact replied" is "True"', type: 'branch', color: '#8B5CF6' },
  { id: 'no-reply', row: 1, col: 2, icon: GitBranch, label: "Contact didn't reply", subtitle: 'When none of the conditions are met', type: 'branch', color: '#8B5CF6' },
  
  // Row 3 - Actions
  { id: 'update-opp', row: 2, col: 0, icon: DollarSign, label: 'Update Opportunity', subtitle: 'to Hot Lead', type: 'action', color: '#10B981' },
  { id: 'call', row: 2, col: 2, icon: Phone, label: 'Call Connect', type: 'action', color: '#3B82F6' },
  
  // Row 4 - Condition
  { id: 'check-reply', row: 3, col: 0, icon: Code, label: 'Was the reply positive?', type: 'condition', color: '#6B7280' },
  { id: 'voicemail', row: 3, col: 2, icon: MessageSquare, label: 'Voicemail', type: 'action', color: '#F59E0B' },
  
  // Row 5 - Final branches
  { id: 'positive', row: 4, col: -0.5, icon: GitBranch, label: 'Positive', subtitle: 'If "Intent" is "Yes"', type: 'branch', color: '#22C55E' },
  { id: 'negative', row: 4, col: 0.5, icon: GitBranch, label: 'Negative', subtitle: 'If "Intent" is not "Yes"', type: 'branch', color: '#EF4444' },
  { id: 'wait', row: 4, col: 2, icon: Clock, label: 'Wait 1 day', type: 'action', color: '#F59E0B' },
  
  // Row 6 - Final actions
  { id: 'booking-sms', row: 5, col: -0.5, icon: MessageSquare, label: 'Booking Link SMS', type: 'action', color: '#10B981' },
  { id: 'survey-sms', row: 5, col: 0.5, icon: Mail, label: 'Survey Link SMS', type: 'action', color: '#10B981' },
  { id: 'followup-sms', row: 5, col: 2, icon: MessageSquare, label: 'Any questions SMS', type: 'action', color: '#10B981' },
];

// Connections between nodes (from -> to)
const connections = [
  { from: 'trigger', to: 'replied' },
  { from: 'trigger', to: 'no-reply' },
  { from: 'replied', to: 'update-opp' },
  { from: 'no-reply', to: 'call' },
  { from: 'update-opp', to: 'check-reply' },
  { from: 'call', to: 'voicemail' },
  { from: 'check-reply', to: 'positive' },
  { from: 'check-reply', to: 'negative' },
  { from: 'voicemail', to: 'wait' },
  { from: 'positive', to: 'booking-sms' },
  { from: 'negative', to: 'survey-sms' },
  { from: 'wait', to: 'followup-sms' },
];

const WorkflowsContent: React.FC<WorkflowsContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Calculate node positions
  const getNodePosition = (node: typeof workflowNodes[0]) => {
    const rowHeight = 52;
    const colWidth = 130;
    const startX = 180;
    const startY = 15;
    
    return {
      x: startX + (node.col * colWidth),
      y: startY + (node.row * rowHeight)
    };
  };

  return (
    <div 
      className="flex-1 p-3 overflow-hidden relative"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between mb-2"
        style={{ animation: `slideInFromTop 0.3s ease-out ${animationDelay + 100}ms both` }}
      >
        <div>
          <h3 
            className="text-xs font-semibold"
            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
          >
            Lead Response Workflow
          </h3>
          <p className="text-[9px]" style={{ color: isDark ? '#888888' : '#666666' }}>
            Automated follow-up sequence
          </p>
        </div>
        <div 
          className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium"
          style={{ 
            backgroundColor: isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.1)',
            color: '#22C55E'
          }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" style={{ animation: 'pulse 2s infinite' }} />
          Active
        </div>
      </div>

      {/* Workflow Canvas */}
      <div 
        className="relative rounded-lg overflow-hidden"
        style={{ 
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          animation: `scaleIn 0.4s ease-out ${animationDelay + 200}ms both`,
          height: '320px',
          backgroundImage: isDark 
            ? 'radial-gradient(circle, #333333 1px, transparent 1px)'
            : 'radial-gradient(circle, #E5E5E5 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      >
        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
          {connections.map((conn, index) => {
            const fromNode = workflowNodes.find(n => n.id === conn.from);
            const toNode = workflowNodes.find(n => n.id === conn.to);
            if (!fromNode || !toNode) return null;

            const fromPos = getNodePosition(fromNode);
            const toPos = getNodePosition(toNode);
            
            // Create path with vertical then horizontal movement
            const midY = fromPos.y + 35;
            const path = `M ${fromPos.x + 45} ${fromPos.y + 28} 
                          L ${fromPos.x + 45} ${midY}
                          L ${toPos.x + 45} ${midY}
                          L ${toPos.x + 45} ${toPos.y}`;

            return (
              <g key={index}>
                <path
                  d={path}
                  fill="none"
                  stroke={isDark ? '#444444' : '#D1D5DB'}
                  strokeWidth="1.5"
                  style={{
                    animation: `fadeIn 0.5s ease-out ${animationDelay + 300 + (index * 40)}ms both`
                  }}
                />
                {/* Connection dot */}
                <circle
                  cx={toPos.x + 45}
                  cy={toPos.y}
                  r="3"
                  fill={isDark ? '#555555' : '#D1D5DB'}
                  style={{
                    animation: `scaleIn 0.3s ease-out ${animationDelay + 350 + (index * 40)}ms both`
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* Nodes */}
        {workflowNodes.map((node, index) => {
          const Icon = node.icon;
          const pos = getNodePosition(node);
          const isBranch = node.type === 'branch';
          const isCondition = node.type === 'condition';
          
          return (
            <div
              key={node.id}
              className="absolute flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
              style={{
                left: pos.x,
                top: pos.y,
                width: '90px',
                backgroundColor: isDark ? '#252525' : '#FFFFFF',
                border: `1px solid ${isBranch ? node.color : (isDark ? '#3A3A3A' : '#E0E0E0')}`,
                borderTopColor: isBranch ? node.color : undefined,
                borderTopWidth: isBranch ? '2px' : '1px',
                animation: `scaleIn 0.3s ease-out ${animationDelay + 250 + (index * 50)}ms both`,
                zIndex: 1,
                boxShadow: isDark 
                  ? '0 2px 4px rgba(0,0,0,0.3)' 
                  : '0 2px 4px rgba(0,0,0,0.08)'
              }}
            >
              <div 
                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                style={{ 
                  backgroundColor: `${node.color}20`,
                }}
              >
                <Icon className="w-3 h-3" style={{ color: node.color }} />
              </div>
              <div className="flex-1 min-w-0 flex items-center justify-between">
                <span 
                  className="text-[8px] font-medium truncate leading-tight"
                  style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                >
                  {node.label}
                </span>
                <MoreHorizontal 
                  className="w-2.5 h-2.5 flex-shrink-0 ml-0.5" 
                  style={{ color: isDark ? '#555555' : '#AAAAAA' }} 
                />
              </div>
            </div>
          );
        })}

        {/* Animated flow dots */}
        <div 
          className="absolute w-2 h-2 rounded-full"
          style={{
            backgroundColor: '#8B5CF6',
            left: '225px',
            top: '50px',
            animation: 'flowDown 4s ease-in-out infinite',
            boxShadow: '0 0 8px rgba(139,92,246,0.5)'
          }}
        />
      </div>

      {/* Stats bar */}
      <div 
        className="grid grid-cols-3 gap-2 mt-2"
        style={{ animation: `slideInFromBottom 0.4s ease-out ${animationDelay + 600}ms both` }}
      >
        {[
          { label: 'Leads Processed', value: '1,247' },
          { label: 'Responses', value: '892' },
          { label: 'Conversion', value: '24.8%' },
        ].map((stat, index) => (
          <div 
            key={index}
            className="p-1.5 rounded-md text-center"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
            }}
          >
            <div 
              className="text-[10px] font-bold"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              {stat.value}
            </div>
            <div 
              className="text-[8px]"
              style={{ color: isDark ? '#888888' : '#666666' }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowsContent;
